import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Select,
    DatePicker,
    Button,
    Checkbox,
} from 'antd';
import {
    ChevronDown,
    ChevronRight,
    Calendar,
    X,
    Plus,
    RefreshCw,
    UploadCloud
} from 'lucide-react';
import { LeftOutlined } from "@ant-design/icons";

import Layout from '../../Layout';
import { Link } from "react-router-dom";

export default function AddAllocationForm() {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // ================= STATES =================
    const [expandedSections, setExpandedSections] = useState({
        general: true,
        customFields: true,
        maintenance: true,
        relatedObjects: true,
    });

    const toggleSection = (key: string) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // State cho phần "Đối tượng liên quan"
    const [relatedObjects, setRelatedObjects] = useState([
        { id: 1, object: undefined }
    ]);

    const handleAddRelatedObject = () => {
        setRelatedObjects([...relatedObjects, { id: Date.now(), object: undefined }]);
    };

    const handleRemoveRelatedObject = (id: number) => {
        setRelatedObjects(relatedObjects.filter(item => item.id !== id));
    };

    // ================= MOCK DATA CHO CÁC Ô SELECT =================
    const unitOptions = [
        { value: 'cai', label: 'Cái' },
        { value: 'bo', label: 'Bộ' },
        { value: 'chiec', label: 'Chiếc' },
    ];

    const categoryOptions = [
        { value: 'laptop', label: 'Máy tính xách tay' },
        { value: 'dieuhoa', label: 'Điều hòa' },
    ];

    const locationOptions = [
        { value: 'hcm', label: 'HCM' },
        { value: 'hcm1', label: 'HCM - Lầu 1' },
    ];

    const ownerOptions = [
        { value: 'cty', label: 'Công ty Cổ phần Khaco' },
    ];

    const departmentOptions = [
        { value: 'kd', label: 'Phòng kinh doanh' },
        { value: 'kt', label: 'Phòng kỹ thuật' },
    ];

    const relatedObjectOptions = [
        { value: 'obj1', label: 'Đối tượng 1' },
        { value: 'obj2', label: 'Đối tượng 2' },
    ];

    // ================= THEME =================
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
            controlHeight: 40,
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const SectionHeader = ({ title, sectionKey }: { title: string; sectionKey: keyof typeof expandedSections }) => (
        <div
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-8 first:mt-0 cursor-pointer hover:opacity-80 transition-opacity select-none"
        >
            {expandedSections[sectionKey] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span>{title}</span>
        </div>
    );

    return (
        <Layout>
            {/* Header cố định */}
            <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 md:px-6 py-4 sticky top-[64px] z-10 shadow-sm">
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/assets/list" className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white">
                            <LeftOutlined className="text-[10px]" />
                        </div>
                    </Link>
                    <span className="text-gray-800 dark:text-gray-200 font-medium">Tạo mới tài sản</span>
                </div>
            </div>

            <ConfigProvider theme={antdThemeConfig}>
                <div className="min-h-screen pb-10 transition-colors duration-300">
                    <div className="w-full mx-auto rounded-lg shadow-sm p-4 md:p-6 mt-6 dark:bg-gray-800 bg-white border dark:border-gray-700">

                        {/* --- 1. THÔNG TIN CHUNG --- */}
                        <SectionHeader title="Thông tin chung" sectionKey="general" />
                        {expandedSections.general && (
                            <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 mb-6">

                                {/* Dòng 1: Mã TS, Số lượng, Đơn vị */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Mã TS <span className="text-red-500">*</span></label>
                                        <Input defaultValue="TS.248" suffix={<RefreshCw size={16} className="text-gray-400 cursor-pointer hover:text-[#3e91ab]" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Số lượng <span className="text-red-500">*</span></label>
                                        <Input placeholder="Số lượng" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Đơn vị <span className="text-red-500">*</span></label>
                                        <Select placeholder="Chọn" className="w-full" options={unitOptions} allowClear />
                                    </div>
                                </div>

                                {/* Checkbox: Nhân bản tài sản */}
                                <div className="flex items-center gap-2">
                                    <Checkbox />
                                    <span className="text-xs text-gray-700 dark:text-gray-300">Nhân bản tài sản</span>
                                </div>

                                {/* Dòng 2: Tên tài sản & Model / Series */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Tên tài sản <span className="text-red-500">*</span></label>
                                        <Input placeholder="Nhập tên tài sản" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Model / Series</label>
                                        <Input placeholder="Model / Series" />
                                    </div>
                                </div>

                                {/* Dòng 3: Loại tài sản & Barcode */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Loại tài sản</label>
                                        <Select placeholder="Chọn loại tài sản" className="w-full" options={categoryOptions} allowClear />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Barcode</label>
                                        <Input placeholder="Barcode" />
                                    </div>
                                </div>

                                {/* Dòng 4: Ngày mua, Thời gian BH, Ngày kết thúc BH */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Ngày mua <span className="text-red-500">*</span></label>
                                        <DatePicker className="w-full" format="DD/MM/YYYY" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Thời gian BH (tháng)</label>
                                        <Input placeholder="Thời gian BH" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Ngày kết thúc BH</label>
                                        <DatePicker className="w-full" format="DD/MM/YYYY" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                    </div>
                                </div>

                                {/* Dòng 5: Giá mua & Khấu hao */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Giá mua</label>
                                        <Input placeholder="Giá mua" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Khấu hao (tháng)</label>
                                        <Input placeholder="Khấu hao (tháng)" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Nhà cung cấp</label>
                                    <Input placeholder="Nhà cung cấp" />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Địa chỉ</label>
                                    <Input placeholder="Địa chỉ" />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Điện thoại</label>
                                    <Input placeholder="Điện thoại" />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Vị trí tài sản</label>
                                    <Select placeholder="Chọn vị trí" className="w-full" options={locationOptions} allowClear />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Pháp nhân sở hữu</label>
                                    <Select placeholder="Chọn pháp nhân" className="w-full" options={ownerOptions} allowClear />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Phòng ban quản lý <span className="text-red-500">*</span></label>
                                    <Select placeholder="Chọn phòng ban" className="w-full" options={departmentOptions} allowClear />
                                </div>

                                {/* Ảnh tài sản */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Ảnh tài sản</label>
                                    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 w-32 h-32 flex flex-col items-center justify-center cursor-pointer hover:border-[#3e91ab]">
                                        <UploadCloud size={24} className="text-gray-400 mb-1" />
                                        <span className="text-[11px] text-gray-400">Tải ảnh lên</span>
                                    </div>
                                </div>

                                {/* Mô tả */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Mô tả</label>
                                    <Input.TextArea placeholder="Nhập mô tả" rows={3} />
                                </div>

                                {/* Đính kèm */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Đính kèm</label>
                                    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 flex flex-col items-center justify-center gap-3 bg-gray-50/50 dark:bg-gray-900/20">
                                        <UploadCloud size={32} className="text-gray-400" />
                                        <span className="text-xs text-gray-500">Kéo thả file vào đây để tải lên hoặc</span>
                                        <div className="flex gap-2">
                                            <Button type="primary" size="small" style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }}>
                                                CHỌN TỪ MÁY
                                            </Button>
                                            <Button size="small">CHỌN TỪ CLOUD</Button>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* --- 2. TRƯỜNG TÙY BIẾN --- */}
                        <SectionHeader title="Trường tùy biên" sectionKey="customFields" />
                        {expandedSections.customFields && (
                            <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 mb-6">
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Địa điểm</label>
                                    <Input placeholder="Địa điểm" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Nước sản xuất</label>
                                    <Input placeholder="Nước sản xuất" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Năm sản xuất</label>
                                    <Input placeholder="Năm sản xuất" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Năm sử dụng</label>
                                    <Input placeholder="Năm sử dụng" />
                                </div>
                            </div>
                        )}

                        {/* --- 3. BẢO TRÌ / BẢO DƯỠNG --- */}
                        <SectionHeader title="Bảo trì/Bảo dưỡng" sectionKey="maintenance" />
                        {expandedSections.maintenance && (
                            <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 mb-6">
                                <div className="flex gap-6">
                                    <Checkbox><span className="text-xs text-gray-700 dark:text-gray-300">Nội bộ</span></Checkbox>
                                    <Checkbox><span className="text-xs text-gray-700 dark:text-gray-300">Hãng</span></Checkbox>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Chu kỳ BTBD</label>
                                    <Input placeholder="Chu kỳ BTBD" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Thời gian kiểm định</label>
                                    <DatePicker className="w-full" format="DD/MM/YYYY" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Không kiểm định</label>
                                    <Input placeholder="Không kiểm định" />
                                </div>
                            </div>
                        )}

                        {/* --- 4. ĐỐI TƯỢNG LIÊN QUAN --- */}
                        <SectionHeader title="Đối tượng liên quan" sectionKey="relatedObjects" />
                        {expandedSections.relatedObjects && (
                            <div className="animate-in slide-in-from-top-2 mb-6">
                                {relatedObjects.map((item) => (
                                    <div key={item.id} className="flex items-center gap-2 mb-3">
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="Đối tượng liên quan"
                                            className="w-full"
                                            value={item.object}
                                            onChange={(val) => {
                                                setRelatedObjects(relatedObjects.map(o => o.id === item.id ? { ...o, object: val } : o));
                                            }}
                                            options={relatedObjectOptions}
                                        />
                                        {relatedObjects.length > 1 && (
                                            <X size={18} className="text-gray-400 cursor-pointer hover:text-red-500 shrink-0" onClick={() => handleRemoveRelatedObject(item.id)} />
                                        )}
                                    </div>
                                ))}

                                <button
                                    onClick={handleAddRelatedObject}
                                    className="flex items-center justify-center w-6 h-6 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mt-2"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        )}

                        {/* --- FOOTER BUTTONS --- */}
                        <div className="flex gap-3 pt-6 mt-10 border-t dark:border-gray-700">
                            <Button type="primary" style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab', fontWeight: 500 }}>
                                CẬP NHẬT
                            </Button>
                            <Button className="font-medium">
                                HỦY BỎ
                            </Button>
                        </div>

                    </div>
                </div>
            </ConfigProvider>
        </Layout>
    );
}